// Phase 2 (grade-2 ceiling) — `place_value_blocks` concept visual.
//
// Renders base-10 blocks for a number: hundreds FLATS (10×10 squares), tens
// RODS (1×10 columns) and ones CUBES (single squares) — the standard
// place-value manipulative. ADDITIVE — registers itself under the
// "place_value_blocks" kind; no skill targets it yet, so it stays invisible
// to kids until content opts in. Plugs in with NO edit to MathVisual or the
// session screens (the registry resolves it by key).
//
// READS its visualData via the shared MathRendererProps contract: it takes
// the number from `total` (the value to decompose) and splits it into
//   hundreds = ⌊total/100⌋ % 10,  tens = ⌊total/10⌋ % 10,  ones = total % 10
// — i.e. the digits of a 0–999 number. `a`/`op`/`b` are unused. A non-finite
// or negative total clamps to 0 so it always renders (fail-safe, never NaN).
import type { MathRendererProps } from "./rendererRegistry";

const FLAT = "var(--pal-green, #5a9e6f)"; // hundreds
// MA-707 — ROD/CUBE/STROKE/U, the rod/cube placement helpers and the
// place-count phrase below are EXPORTED (not copied) so `column_add`
// (ColumnAddRenderer.tsx) draws the identical base-ten geometry and speaks
// the identical MA-658 aria grammar. Factoring only — this renderer's own
// markup is byte-identical before/after (pinned by
// scripts/audit/columnAddIntegrityTest.mjs's place_value_blocks digest pin).
export const ROD = "var(--pal-orange, #d97742)"; // tens
export const CUBE = "var(--color-text-secondary, #7a6f60)"; // ones
export const STROKE = "var(--color-border-secondary, #cdbfae)";

export const U = 7; // px per unit cube edge
const GAP = 16; // gap between block groups

export type BlockRect = { x: number; y: number; w: number; h: number; fill: string };

/** Tens rods (1 unit wide, 10 tall), left to right from `x`, top at `y`.
 *  Returns the x just past the last rod's trailing 3px spacer. */
export function pushTensRods(rects: BlockRect[], x: number, count: number, y = 0): number {
  for (let i = 0; i < count; i++) {
    rects.push({ x, y, w: U, h: 10 * U, fill: ROD });
    x += U + 3;
  }
  return x;
}

/** Ones cubes (single units) stacked bottom-up in ONE column at `x`, inside
 *  a rod-tall (10-unit) band whose top is `y` — the same column a rod
 *  occupies, so a full stack of 9 sits one cube short of a rod. */
export function pushOnesCubes(rects: BlockRect[], x: number, count: number, y = 0): void {
  for (let i = 0; i < count; i++) {
    rects.push({ x, y: y + (9 - i) * U, w: U, h: U, fill: CUBE });
  }
}

/** MA-658 grammar: "1 ten" / "3 tens", "1 one" / "7 ones" — never "1 tens". */
export function placeCountPhrase(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/** MA-694 — the `displayStyle` the mapper puts on a place-value picture whose
 *  number the PROMPT already states ("How many tens are in 34?" — the
 *  read-shape branch of `buildMathVisual`, whose `total` is the prompt's own
 *  subject and is never the answer). For that picture the aria says what the
 *  picture is and gives NO count and NO numeral: "3 tens, 4 ones" read the
 *  answer to a screen-reader child, and the MA-658 rule (the aria never speaks
 *  the composed numeral) still holds. Absent => the MA-658 enumerating aria,
 *  unchanged (the ms.math.13 read items, where the blocks are the only source
 *  of the number). */
export const PLACE_VALUE_SUBJECT_STATED = "subject_stated";
/** MA-694 — the aria for a PLACE_VALUE_SUBJECT_STATED picture: the same
 *  sentence for every number, so neither a count, nor a missing place, nor
 *  the numeral itself can be read off it. */
export const PLACE_VALUE_SUBJECT_STATED_LABEL = "Place value blocks showing the number in the question.";

function PlaceValueBlocksRenderer({ total, displayStyle }: MathRendererProps) {
  // Clamp to the 0–999 place-value scope (grade-2). A value >=1000 would
  // otherwise drop its thousands digit (`% 10`) and render a misleadingly
  // small number; clamping to 999 degrades safely and visibly instead.
  const raw = Number.isFinite(total) ? Math.max(0, Math.floor(total)) : 0;
  const n = Math.min(raw, 999);
  const hundreds = Math.floor(n / 100) % 10;
  const tens = Math.floor(n / 10) % 10;
  const ones = n % 10;

  const flatSize = 10 * U; // 70px square

  const rects: BlockRect[] = [];
  let x = 0;
  // Hundreds flats (drawn as a filled 10×10 square with a subtle grid look).
  for (let i = 0; i < hundreds; i++) {
    rects.push({ x, y: 0, w: flatSize, h: flatSize, fill: FLAT });
    x += flatSize + 4;
  }
  if (hundreds) x += GAP - 4;
  // Tens rods (1 unit wide, 10 tall).
  x = pushTensRods(rects, x, tens);
  if (tens) x += GAP - 3;
  // Ones cubes (single units, stacked in a column).
  pushOnesCubes(rects, x, ones);
  if (ones) x += U;

  const width = Math.max(U, x);
  const height = flatSize;
  // MA-658: the aria label describes the BLOCK STRUCTURE only — it must
  // never speak the composed numeral `n`. This renderer is shared by a
  // READ item (ms.math.20/ms.math.13, where the picture IS the question
  // and the decomposition is legitimately spoken — that stays) and, until
  // MA-658, a COMPOSE item (ms.math.21) whose mapper drew the ANSWER into
  // `total`; speaking "for ${n}" there read the answer aloud to an AT
  // child. Dropping the numeral clause is safe for every caller: it was
  // always redundant for a read item (the tens/ones clause already names
  // the value) and is the fix itself for a compose item. The hundreds
  // clause is omitted entirely at 0 (this pack never reaches the hundreds
  // place) rather than reading "0 hundreds"; tens/ones stay singular at 1
  // ("1 ten" / "1 one") instead of the ungrammatical "1 tens" / "1 ones".
  const parts: string[] = [];
  if (hundreds > 0) {
    parts.push(`${hundreds} ${hundreds === 1 ? "hundred" : "hundreds"}`);
  }
  parts.push(placeCountPhrase(tens, "ten", "tens"));
  parts.push(placeCountPhrase(ones, "one", "ones"));
  // MA-694 — the prompt states `n` itself (see PLACE_VALUE_SUBJECT_STATED):
  // say what the picture is, never how many of a place it has (the item asks
  // exactly that), never by omitting a zero place, never the numeral.
  const ariaLabel = displayStyle === PLACE_VALUE_SUBJECT_STATED
    ? PLACE_VALUE_SUBJECT_STATED_LABEL
    : `Place value blocks: ${parts.join(", ")}`;
  return (
    <svg
      viewBox={`0 0 ${width + 2} ${height + 2}`}
      width="100%"
      style={{ maxWidth: Math.min(360, width + 2) }}
      role="img"
      aria-label={ariaLabel}
    >
      {blockRectElements(rects)}
    </svg>
  );
}

/** The <rect> elements for a laid-out block set, offset by the 1px stroke
 *  margin — shared with ColumnAddRenderer so both draw identical blocks. */
export function blockRectElements(rects: BlockRect[], keyPrefix = "") {
  return rects.map((r, i) => (
    <rect
      key={`${keyPrefix}${i}`}
      x={r.x + 1}
      y={r.y + 1}
      width={r.w}
      height={r.h}
      rx="1"
      fill={r.fill}
      stroke={STROKE}
      strokeWidth="1"
    />
  ));
}

export default PlaceValueBlocksRenderer;
